/** Pure factory: builds an EnemySkillRegistry from an archetype's skill definitions and tuning. */
import type { EnemyArchetypeConfig } from '../../utils/Types';
import { EnemySkillRegistry } from './EnemySkillRegistry';
import { EnemyMeleeSkillEffect } from './EnemyMeleeSkillEffect';
import { EnemyDashSkillEffect } from './EnemyDashSkillEffect';
import { EnemyRangedSkillEffect } from './EnemyRangedSkillEffect';
import { EnemyBlockSkillEffect } from './EnemyBlockSkillEffect';
import { EnemyAreaSkillEffect } from './EnemyAreaSkillEffect';
import { EnemyBoltSkillEffect } from './EnemyBoltSkillEffect';
import { EnemyBuffSkillEffect } from './EnemyBuffSkillEffect';
import { EnemyBackupSkillEffect } from './EnemyBackupSkillEffect';
import { EnemyStamina } from './EnemyStamina';

/** Build an EnemySkillRegistry from the archetype's skill definitions and tuning. */
export function buildSkillRegistry(config: EnemyArchetypeConfig): EnemySkillRegistry {
  const registry = new EnemySkillRegistry();

  // Build stamina pool if the archetype defines one (required for dash skills)
  let stamina: EnemyStamina | null = null;
  if (config.stamina) {
    stamina = new EnemyStamina(config.stamina);
    registry.setStamina(stamina);
  }

  for (const skillDef of config.skills) {
    const tuning = config.skillTuning?.[skillDef.id] ?? {};
    switch (skillDef.type) {
      case 'melee':
        registry.register(new EnemyMeleeSkillEffect(skillDef.id, {
          windupMs: tuning.windupMs as number,
          activeDurationMs: (tuning.activeDurationMs as number) ?? 80,
          recoveryMs: tuning.recoveryMs as number,
          cooldownMs: (tuning.cooldownMs as number) ?? 0,
          attackRange: tuning.attackRange as number,
          baseDamage: (tuning.baseDamage as number) ?? config.baseDamage,
          attackType: (tuning.attackType as string) ?? config.attackType ?? 'punch',
          interruptibleDuringWindup: (tuning.interruptibleDuringWindup as boolean) ?? true,
        }));
        break;
      case 'dash':
        if (!stamina) throw new Error(`Archetype ${config.id} has dash skill but no stamina config`);
        registry.register(new EnemyDashSkillEffect(skillDef.id, {
          dashSpeed: (tuning.dashSpeed as number) ?? 300,
          dashDurationMs: (tuning.dashDurationMs as number) ?? 150,
          windupMs: (tuning.windupMs as number) ?? 100,
          recoveryMs: (tuning.recoveryMs as number) ?? 200,
          cooldownMs: (tuning.cooldownMs as number) ?? 3000,
          staminaCost: (tuning.staminaCost as number) ?? 50,
          contactDamage: (tuning.contactDamage as number) ?? 0,
          attackType: (tuning.attackType as string) ?? config.attackType ?? 'punch',
          dashDirection: (tuning.dashDirection as 'toward' | 'away') ?? 'toward',
        }, stamina));
        break;
      case 'ranged':
        registry.register(new EnemyRangedSkillEffect(skillDef.id, {
          windupMs: (tuning.windupMs as number) ?? 600,
          recoveryMs: (tuning.recoveryMs as number) ?? 400,
          cooldownMs: (tuning.cooldownMs as number) ?? 2000,
          range: tuning.range as number,
          projectileSpeed: (tuning.projectileSpeed as number) ?? 200,
          projectileDamage: (tuning.projectileDamage as number) ?? config.baseDamage,
          projectileLifetimeMs: (tuning.projectileLifetimeMs as number) ?? 3000,
          projectileSize: (tuning.projectileSize as number) ?? 6,
          projectileColor: (tuning.projectileColor as number) ?? 0x33cc66,
          attackType: (tuning.attackType as string) ?? config.attackType ?? 'punch',
        }));
        break;
      case 'block':
        if (!stamina) throw new Error(`Archetype ${config.id} has block skill but no stamina config`);
        registry.register(new EnemyBlockSkillEffect(skillDef.id, {
          blockDurationMs: (tuning.blockDurationMs as number) ?? 2000,
          blockAngle: (tuning.blockAngle as number) ?? 90,
          damageReduction: (tuning.damageReduction as number) ?? 0.9,
          cooldownMs: (tuning.cooldownMs as number) ?? 3000,
          staminaCost: (tuning.staminaCost as number) ?? 25,
        }, stamina));
        break;
      case 'area':
        registry.register(new EnemyAreaSkillEffect(skillDef.id, {
          areaRadius: (tuning.areaRadius as number) ?? 128,
          areaDamagePerTick: (tuning.areaDamagePerTick as number) ?? 5,
          tickIntervalMs: (tuning.tickIntervalMs as number) ?? 500,
          windupMs: (tuning.windupMs as number) ?? 800,
          activeDurationMs: (tuning.activeDurationMs as number) ?? 3000,
          recoveryMs: (tuning.recoveryMs as number) ?? 500,
          cooldownMs: (tuning.cooldownMs as number) ?? 5000,
          range: tuning.range as number,
          attackType: (tuning.attackType as string) ?? config.attackType ?? 'punch',
        }));
        break;
      case 'bolt':
        registry.register(new EnemyBoltSkillEffect(skillDef.id, {
          boltDamage: (tuning.boltDamage as number) ?? config.baseDamage,
          boltRange: tuning.boltRange as number,
          windupMs: (tuning.windupMs as number) ?? 400,
          recoveryMs: (tuning.recoveryMs as number) ?? 300,
          cooldownMs: (tuning.cooldownMs as number) ?? 2000,
          attackType: (tuning.attackType as string) ?? config.attackType ?? 'punch',
        }));
        break;
      case 'buff':
        registry.register(new EnemyBuffSkillEffect(skillDef.id, {
          buffRadius: (tuning.buffRadius as number) ?? config.buffRadius ?? 128,
          buffDurationMs: (tuning.buffDurationMs as number) ?? 5000,
          buffDamageMultiplier: (tuning.buffDamageMultiplier as number) ?? config.buffDamageMultiplier ?? 1.3,
          buffSpeedMultiplier: (tuning.buffSpeedMultiplier as number) ?? config.buffSpeedMultiplier ?? 1.15,
          windupMs: (tuning.windupMs as number) ?? 300,
          recoveryMs: (tuning.recoveryMs as number) ?? 200,
          cooldownMs: (tuning.cooldownMs as number) ?? 8000,
        }));
        break;
      case 'backup':
        registry.register(new EnemyBackupSkillEffect(skillDef.id, {
          backupRadius: (tuning.backupRadius as number) ?? config.backupRadius ?? 256,
          windupMs: (tuning.windupMs as number) ?? 500,
          recoveryMs: (tuning.recoveryMs as number) ?? 300,
          cooldownMs: (tuning.cooldownMs as number) ?? 10000,
        }));
        break;
    }
  }
  return registry;
}
